import clsx from 'clsx';
import React, { FC } from 'react';

import {
  COMMENTS,
  EMAIL,
  FACEBOOK,
  FLIPBOARD,
  INSTAGRAM,
  LINKEDIN,
  PINTEREST,
  REDDIT,
  STEAM,
  TIKTOK,
  TWITCH,
  TWITTER,
  WEBSITE,
  WHATSAPP,
  YOUTUBE,
} from '~/assets/socialNetworkIcons';
import TextWithBreakpoints from '~/components/text-with-breakpoints/TextWithBreakpoints';
import { IButtonSocial, IButtonSocialIcon } from '~/interfaces/components/ButtonSocial';

export const icons = Object.freeze({
  EMAIL: <EMAIL key="EMAIL" />,
  WEBSITE: <WEBSITE key="WEBSITE" />,
  FACEBOOK: <FACEBOOK key="FACEBOOK" />,
  TWITTER: <TWITTER key="TWITTER" />,
  WHATSAPP: <WHATSAPP key="WHATSAPP" />,
  FLIPBOARD: <FLIPBOARD key="FLIPBOARD" />,
  INSTAGRAM: <INSTAGRAM key="INSTAGRAM" />,
  LINKEDIN: <LINKEDIN key="LINKEDIN" />,
  PINTEREST: <PINTEREST key="PINTEREST" />,
  REDDIT: <REDDIT key="REDDIT" />,
  STEAM: <STEAM key="STEAM" />,
  TIKTOK: <TIKTOK key="TIKTOK" />,
  TWITCH: <TWITCH key="TWITCH" />,
  YOUTUBE: <YOUTUBE key="YOUTUBE" />,
  COMMENTS: <COMMENTS key="COMMENTS" />,
});

export const iconsList = Object.values(icons);

export const iconGetter = (iconName: IButtonSocialIcon, iconsList: JSX.Element[]) => {
  const icon = iconsList.find((icon) => icon.key === iconName);
  return icon || null;
};

const ButtonSocial: FC<IButtonSocial> = ({
  iconName,
  label,
  sideText,
  border,
  href,
  target,
  rel,
}) => {
  const iconToDisplay = iconName ? iconGetter(iconName, iconsList) : null;

  const doesIconRequireExtraClass = (iconName: string) => {
    return ['EMAIL', 'WHATSAPP', 'WEBSITE'].includes(iconName);
  };

  const LabelWithAbbrv = () => {
    if (iconName) {
      return null;
    }
    return (
      <>
        {!iconName && label?.abbreviationFor && label.labelText && (
          <abbr
            title={label.abbreviationFor}
            className="button-social__label button-social__label--abbr"
          >
            {label.labelText}
          </abbr>
        )}
      </>
    );
  };

  const LabelWithoutAbbrv = () => {
    if (iconName) {
      return null;
    }
    return (
      <>
        {!label?.abbreviationFor && label?.labelText && (
          <span className="button-social__label">{label.labelText}</span>
        )}
      </>
    );
  };

  const RegularSocialIcon = () => {
    return (
      <>
        {iconName && doesIconRequireExtraClass(iconName) === false && (
          <span className="button-social__icon">{iconToDisplay}</span>
        )}
      </>
    );
  };

  const IconsWithExtraClass = () => {
    if (iconName && doesIconRequireExtraClass(iconName)) {
      return (
        <>
          <span className={`button-social__icon button-social__icon-${iconName.toLowerCase()}`}>
            {iconToDisplay}
          </span>
        </>
      );
    }
    return null;
  };

  const ResponsiveSideText = () => {
    return (
      <>
        {sideText && (
          <span
            className={`button-social__side-text ${
              iconName === 'COMMENTS' && 'button-social__side-text--comments'
            }`}
          >
            <TextWithBreakpoints
              default={sideText?.default || ''}
              xs={sideText?.xs}
              s={sideText?.s}
              m={sideText?.m}
              l={sideText?.l}
              xl={sideText?.xl}
            />
          </span>
        )}
      </>
    );
  };

  if (!iconName && !label) return null;

  return (
    <a
      className={`button-social ${border && border.left ? 'button-social--border-left' : ''} ${
        border && border.right ? 'button-social--border-right' : ''
      } ${iconName === 'WHATSAPP' ? 'button-social--hide' : ''}`}
      href={href}
      target={target || '_self'}
      rel={rel}
      aria-label={clsx(iconToDisplay?.key ?? label?.labelText)}
    >
      <LabelWithAbbrv />
      <LabelWithoutAbbrv />
      <IconsWithExtraClass />
      <RegularSocialIcon />
      <ResponsiveSideText />
    </a>
  );
};

export default ButtonSocial;
